/* eslint-disable */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable max-len */
import React, { useState, useEffect, useMemo } from 'react';
import Arrow from 'src/assets/arrow-left-black.svg';
import {
  CommonDropdown,
  CommonInput,
  FormErrorWrap,
  RegularButton,
  ICommonDropdownOption,
  FullScreenLoading,
  OutlinedButton,
} from 'pg-ui-library';
import {
  Container,
  CardContainer,
  HelpForm,
  PageTitle,
  FormInputWrapper,
  NavTextLink,
  ArrowBack,
  HeaderItemLeft,
  ModuleLine,
  SearchLink,
} from './createStyles';
import { useFormik, FormikErrors } from 'formik';
import * as yup from 'yup';
import { useAddExpense } from './mutations/expense.mutation';
import { AssignmentApiItem } from 'src/interfaces/invoice';
import { Link, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useQuery } from '@tanstack/react-query';
import InvoiceService from 'src/services/invoice.service';
import WorkWeekDatepicker from 'src/components/work-week-datepicker';
import UploadLink from 'src/components/upload-link';
import { currencySymbol } from 'src/utils/currency';

const commonFieldStyle = {
  width: '100%',
  height: '35px',
  borderRadius: '7px',
  border: '1px solid #ccc',
  paddingTop: '2px',
  paddingBottom: '2px',
  boxSizing: 'border-box',
  fontSize: '1.5rem',
} as const;

export const toISODate = (date?: string | Date | null): string => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

interface ExpenseRow {
  expenseDate: string;
  category: string;
  amount: string;
  receipt: File | null;
  details: string;
}

interface ExpenseFormValues {
  workerId: string;
  assignmentId: string;
  assignmentName: string;
  clientId: string;
  clientName: string;
  companyId: string;
  companyName: string;
  supplierName: string;
  invoiceNumber: string;
  jobTitle: string;
  hireType: string;
  invoiceDate: Date | null;
  periodStart: Date | null;
  periodEnd: Date | null;
  invoiceAmount: string;
  currency: string;
  totalUnits: string;
  totalHours: string;
  billRate: string;
  payRate: string;
  status: string;
  attachments: File[];
  approverEmail: string;
  approverName: string;
  notes: string;
  workWeek: Date | null;
  assignmentStartDate: Date | null;
  assignmentEndDate: Date | null;
  expenses: ExpenseRow[];

  // ✅ NEW FIELDS START
  employmentCategory?: string;
  billingContactName?: string;

  workingCity?: string;
  workingState?: string;
  address1?: string;
  payOvertimeExempt?: string;

  overtimePayRate?: number | string | null;
  hoursPerWeek?: number | string | null;

  contractStartDate: Date | null;
  contractEndDate: Date | null;

  managerName?: string;
  managerEmail?: string;
  coApproverName?: string;
  coApproverEmail?: string;

  assignmentType?: string;
  workerPayCompany?: string;
  payModel?: string;

  expensesEnabled?: boolean;

  candidateCity?: string;
  candidateState?: string;

  postalCode?: string;
  billAddressSameAsWorkAddress?: boolean;

  payCost?: number | string | null;
  projectCode?: string;
  costCenter?: string;
  department?: string;
  merId?: string;
  purchaseOrder?: string;
  assignmentStatus?: string;
  billingPostalCOde?: string;
  glCode?: string;
  externalReferenceId?: string;
  // ✅ NEW FIELDS END
}

const expenseCategories = [
  { name: '', displayName: 'Select' },
  { name: 'AIRFARE', displayName: 'Airfare' },
  { name: 'LODGING', displayName: 'Lodging' },
  { name: 'MEAL', displayName: 'Meal' },
  { name: 'MATERIALS', displayName: 'Materials' },
  { name: 'MILEAGE', displayName: 'Mileage' },
  { name: 'MOBILE', displayName: 'Mobile Device' },
  { name: 'PARKING', displayName: 'Parking/Toll' },
  { name: 'TRANSPORT', displayName: 'Local Transportation' },
  { name: 'OTHER', displayName: 'Other' },
];

const formatOptionLabel = (date: Date) => {
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();

  return `${dayName} ${mm}/${dd}/${yyyy}`;
};

const formatOptionValue = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

const getWeekDateOptions = (selectedDate: Date, minDate?: Date | null, maxDate?: Date | null) => {
  const current = new Date(selectedDate);
  const day = current.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(current);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(current.getDate() + diffToMonday);

  const normalizedMinDate = minDate ? new Date(minDate) : null;
  const normalizedMaxDate = maxDate ? new Date(maxDate) : null;

  if (normalizedMinDate) {
    normalizedMinDate.setHours(0, 0, 0, 0);
  }

  if (normalizedMaxDate) {
    normalizedMaxDate.setHours(0, 0, 0, 0);
  }

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    date.setHours(0, 0, 0, 0);

    return date;
  })
    .filter(date => {
      if (normalizedMinDate && date < normalizedMinDate) return false;
      if (normalizedMaxDate && date > normalizedMaxDate) return false;
      return true;
    })
    .map(date => ({
      key: formatOptionValue(date),
      value: formatOptionValue(date),
      label: formatOptionLabel(date),
    }));
};

const getWorkWeekStartEnd = (date: Date) => {
  const selected = new Date(date);
  const day = selected.getDay(); // Sunday = 0
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(selected);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(selected.getDate() + diffToMonday);

  const sunday = new Date(monday);
  sunday.setHours(23, 59, 59, 999);
  sunday.setDate(monday.getDate() + 6);

  return { monday, sunday };
};

const CreateExpense: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [contractStartDate, setContractStartDate] = useState<Date | null>(null);
  const [contractEndDate, setContractEndDate] = useState<Date | null>(null);
  const [selectedWorkWeekDate, setSelectedWorkWeekDate] = useState<Date | null>(null);
  const [userRole] = useLocalStorage('userRole', null);

  const addExpense = useAddExpense();
  const navigate = useNavigate();

  const { isFetching: isAssignmentFetching, data: assignmentData } = useQuery(
    ['assignment-list-personnal'],
    async () => {
      const result = await InvoiceService.getAssignmentsPersonalNumber();
      return result?.data?.vw_msd_jd_assignments;
    }
  );

  const formatDate = (date?: string | null) => {
    if (!date) return 'Open';
    return date.split('T')[0];
  };

  const assignmentOptions: ICommonDropdownOption[] = useMemo(() => {
    if (!assignmentData?.items) return [];

    return assignmentData.items
      .filter((item: AssignmentApiItem) => item.EXPENSES_ENABLED === '1')
      .map((item: AssignmentApiItem) => ({
        key: item.ASSIGNMENT_ID,
        value: item.ASSIGNMENT_ID,
        label: `${item.JOBTITLE} (${item.ASSIGNMENT_ID}) - ${item.CLIENT_NAME} (${formatDate(
          item.CONTRACT_START_DATE
        )} - ${formatDate(item.CONTRACT_END_DATE)})`,
      }));
  }, [assignmentData]);

  const assignmentOptionsWithBlank: ICommonDropdownOption[] = [
    { key: '', label: 'Select', value: '' },
    ...assignmentOptions,
  ];

  const validationSchema = yup.object({
    assignmentId: yup.string().required('Assignment is required'),
    workWeek: yup.date().nullable().required('Work Week is required'),
    expenses: yup
      .array()
      .of(
        yup.object({
          expenseDate: yup.string().required('Date of Expense is required'),
          category: yup.string().required('Category is required'),
          amount: yup.string().test('amount-or-miles-validation', function (this: yup.TestContext, value) {
            const parent = this.parent as { category?: string };
            const isMileage = parent.category === 'MILEAGE';
            const fieldName = isMileage ? 'Miles' : 'Amount';

            if (!value || !value.trim()) {
              return this.createError({
                message: `${fieldName} is required`,
              });
            }

            if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) {
              return this.createError({
                message: `${fieldName} can have at most 2 decimal digits`,
              });
            }

            if (Number(value) <= 0) {
              return this.createError({
                message: `${fieldName} must be greater than 0`,
              });
            }

            return true;
          }),
        })
      )
      .min(1, 'At least one expense row is required'),
  });

  const formik = useFormik<ExpenseFormValues>({
    initialValues: {
      workerId: '',
      assignmentId: '',
      assignmentName: '',
      supplierName: '',
      invoiceNumber: '',
      jobTitle: '',
      hireType: '',
      invoiceDate: null,
      periodStart: null,
      periodEnd: null,
      invoiceAmount: '',
      currency: '',
      totalUnits: '',
      totalHours: '',
      billRate: '',
      payRate: '',
      status: 'Submitted',
      attachments: [],
      approverEmail: '',
      approverName: '',
      notes: '',
      workWeek: null,

      // ✅ NEW FIELDS
      clientId: '',
      clientName: '',
      companyId: '',
      companyName: '',
      employmentCategory: '',
      billingContactName: '',
      workingCity: '',
      workingState: '',
      address1: '',
      payOvertimeExempt: '',
      overtimePayRate: '',
      hoursPerWeek: '',
      contractStartDate: null,
      contractEndDate: null,
      managerName: '',
      managerEmail: '',
      coApproverName: '',
      coApproverEmail: '',
      assignmentType: '',
      workerPayCompany: '',
      payModel: '',
      expensesEnabled: false,
      candidateCity: '',
      candidateState: '',
      assignmentStartDate: null,
      assignmentEndDate: null,

      postalCode: '',
      billAddressSameAsWorkAddress: false,
      payCost: '',
      projectCode: '',
      costCenter: '',
      department: '',
      merId: '',
      purchaseOrder: '',
      assignmentStatus: '',
      billingPostalCOde: '',
      glCode: '',
      externalReferenceId: '',

      expenses: [
        {
          expenseDate: '',
          category: '',
          amount: '',
          receipt: null,
          details: '',
        },
      ],
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async values => {
      try {
        const formData = new FormData();

        if (selectedWorkWeekDate) {
          const { monday, sunday } = getWorkWeekStartEnd(selectedWorkWeekDate);
          formData.append('weekStart', toISODate(monday));
          formData.append('weekEnd', toISODate(sunday));
        } else {
          formData.append('weekStart', '');
          formData.append('weekEnd', '');
        }

        formData.append('assignmentId', values.assignmentId || '');
        formData.append('assignmentName', values.assignmentName || '');

        formData.append('supplierName', values.supplierName || '');
        formData.append('invoiceDate', values.invoiceDate ? toISODate(values.invoiceDate) : '');
        formData.append('invoiceNumber', values.invoiceNumber || '');
        formData.append('jobTitle', values.jobTitle || '');
        formData.append('hireType', values.hireType || '');
        formData.append('periodStart', values.periodStart ? toISODate(values.periodStart) : '');
        formData.append('periodEnd', values.periodEnd ? toISODate(values.periodEnd) : '');
        formData.append('status', values.status || '');
        formData.append('invoiceAmount', values.invoiceAmount || '');
        formData.append('totalUnits', values.totalUnits || '');
        formData.append('totalHours', values.totalHours || '');
        formData.append('billRate', values.billRate || '');
        formData.append('payRate', values.payRate || '');
        formData.append('approverEmail', values.approverEmail || '');
        formData.append('approverName', values.approverName || '');
        formData.append('notes', values.notes || '');
        formData.append('currency', values.currency || '');

        // --- NEW FIELDS
        formData.append('clientId', values.clientId || '');
        formData.append('clientName', values.clientName || '');
        formData.append('companyName', values.companyName || '');
        formData.append('companyId', values.companyId || '');
        formData.append('employmentCategory', values.employmentCategory || '');
        formData.append('billingContactName', values.billingContactName || '');
        formData.append('workingCity', values.workingCity || '');
        formData.append('workingState', values.workingState || '');
        formData.append('address1', values.address1 || '');

        formData.append('payOvertimeExempt', values.payOvertimeExempt || '');

        formData.append('overtimePayRate', values.overtimePayRate ? String(values.overtimePayRate) : '');

        formData.append('hoursPerWeek', values.hoursPerWeek ? String(values.hoursPerWeek) : '');

        formData.append('contractStartDate', values.contractStartDate ? toISODate(values.contractStartDate) : '');

        formData.append('contractEndDate', values.contractEndDate ? toISODate(values.contractEndDate) : '');

        formData.append('managerName', values.managerName || '');
        formData.append('managerEmail', values.managerEmail || '');
        formData.append('coApproverName', values.coApproverName || '');
        formData.append('coApproverEmail', values.coApproverEmail || '');

        formData.append('assignmentType', values.assignmentType || '');
        formData.append('workerPayCompany', values.workerPayCompany || '');
        formData.append('payModel', values.payModel || '');

        formData.append('expensesEnabled', values.expensesEnabled ? '1' : '0');

        formData.append('candidateCity', values.candidateCity || '');
        formData.append('candidateState', values.candidateState || '');
        formData.append('assignmentStartDate', values.contractStartDate ? toISODate(values.contractStartDate) : '');

        formData.append('postalCode', values.postalCode || '');

        formData.append('billAddressSameAsWorkAddress', values.billAddressSameAsWorkAddress ? '1' : '0');

        formData.append('payCost', values.payCost ? String(values.payCost) : '');

        formData.append('projectCode', values.projectCode || '');
        formData.append('costCenter', values.costCenter || '');
        formData.append('department', values.department || '');
        formData.append('merId', values.merId || '');
        formData.append('purchaseOrder', values.purchaseOrder || '');
        formData.append('assignmentStatus', values.assignmentStatus || '');
        formData.append('billingPostalCOde', values.billingPostalCOde || '');
        formData.append('glCode', values.glCode || '');
        formData.append('externalReferenceId', values.externalReferenceId || '');

        console.log(values.contractStartDate, toISODate(values.contractStartDate));
        if (values.contractEndDate) {
          formData.append('assignmentEndDate', values.contractEndDate ? toISODate(values.contractEndDate) : '');
        }

        formData.append(
          'receipts',
          JSON.stringify(
            values.expenses.map(exp => {
              const base = {
                expenseDate: exp.expenseDate,
                category: exp.category,
                ...(exp.details ? { details: exp.details } : {}),
              };

              if (exp.category === 'MILEAGE') {
                return {
                  ...base,
                  miles: Number(exp.amount),
                };
              }

              return {
                ...base,
                amount: Number(exp.amount),
              };
            })
          )
        );

        values.expenses.forEach((exp, index) => {
          if (exp.receipt) {
            formData.append(`receiptAttachments_${index}`, exp.receipt);
          }
        });

        uploadedFiles.forEach(file => {
          formData.append('expenseAttachments', file);
        });

        await addExpense.mutateAsync({ expense: formData });
        navigate('/expenses');
      } catch (error) {
        console.error('Error during submission of Expense', error);
      }
    },
  });

  const dateOfExpenseOptions: ICommonDropdownOption[] = useMemo(() => {
    if (!selectedWorkWeekDate) {
      return [{ key: '', value: '', label: 'Select day' }];
    }

    return [
      { key: '', value: '', label: 'Select day' },
      ...getWeekDateOptions(selectedWorkWeekDate, formik.values.contractStartDate, formik.values.contractEndDate),
    ];
  }, [selectedWorkWeekDate, formik.values.contractStartDate, formik.values.contractEndDate]);

  useEffect(() => {
    // Exit if data isn't ready
    if (!assignmentData?.items?.length) return;

    // Optional: you can do something when assignment data loads
    // but DO NOT auto-select the first assignment
    // For example, you could just reset contract dates or leave empty
    setContractStartDate(null);
    setContractEndDate(null);
  }, [assignmentData]);

  const getEmptyExpenseRow = (): ExpenseRow => ({
    expenseDate: '',
    category: '',
    amount: '',
    receipt: null,
    details: '',
  });

  const handleAssignmentChange = (assignmentId: string) => {
    const selected = assignmentData?.items?.find((a: AssignmentApiItem) => a.ASSIGNMENT_ID === assignmentId);

    const expensesLength = formik.values.expenses?.length || 1;
    const resetExpenses = Array.from({ length: expensesLength }, () => getEmptyExpenseRow());

    if (!selected) {
      setContractStartDate(null);
      setContractEndDate(null);
      setSelectedWorkWeekDate(null);
      setUploadedFiles([]);

      formik.resetForm({
        values: {
          ...formik.initialValues,
          workWeek: null,
          expenses: resetExpenses,
          attachments: [],
        },
      });

      return;
    }

    const nextContractStartDate = selected.CONTRACT_START_DATE ? new Date(selected.CONTRACT_START_DATE) : null;

    const nextContractEndDate = selected.CONTRACT_END_DATE ? new Date(selected.CONTRACT_END_DATE) : null;

    setContractStartDate(nextContractStartDate);
    setContractEndDate(nextContractEndDate);
    setSelectedWorkWeekDate(null);
    setUploadedFiles([]);

    formik.resetForm({
      values: {
        ...formik.initialValues,

        workerId: selected.EMPLOYEEID ?? '',
        assignmentId: selected.ASSIGNMENT_ID ?? '',
        assignmentName: `${selected.JOBTITLE} (${selected.ASSIGNMENT_ID}) - ${selected.CLIENT_NAME}`,
        clientId: selected.JOB_CLIENT_ID ?? '',
        clientName: selected.CLIENT_NAME ?? '',
        companyId: selected.COMPANY_ID ?? '',
        companyName: selected.COMPANY_NAME ?? '',
        supplierName: '',
        invoiceNumber: '',
        jobTitle: selected.JOBTITLE ?? '',
        hireType: selected.HIRETYPE ?? '',
        invoiceDate: null,
        periodStart: null,
        periodEnd: null,
        invoiceAmount: '',
        currency: selected.CURRENCY ?? '',
        totalUnits: '',
        totalHours: '',
        billRate: String(selected.BILL_RATE ?? ''),
        payRate: String(selected.PAY_RATE ?? ''),
        status: 'Submitted',
        attachments: [],
        approverEmail: selected.APPROVER_EMAIL ?? '',
        approverName: selected.ASSIGNMENT_APPROVER_NAME ?? '',
        notes: '',
        workWeek: null,
        assignmentStartDate: selected.CONTRACT_START_DATE ? new Date(selected.CONTRACT_START_DATE) : null,
        assignmentEndDate: selected.CONTRACT_END_DATE ? new Date(selected.CONTRACT_END_DATE) : null,

        employmentCategory: selected.EMPLOYMENT_CATEGORY ?? '',
        billingContactName: selected.BILLING_CONTACT_NAME ?? '',
        workingCity: selected.WORKING_CITY ?? '',
        workingState: selected.WORKING_STATE ?? '',
        address1: selected.ADDRESS1 ?? '',
        payOvertimeExempt: selected.PAY_OVERTIME_EXEMPT ?? '',
        overtimePayRate:
          selected.OVERTIME_PAY_RATE !== undefined && selected.OVERTIME_PAY_RATE !== null
            ? parseFloat(selected.OVERTIME_PAY_RATE)
            : '',
        hoursPerWeek:
          selected.HOURSPERWEEK !== undefined && selected.HOURSPERWEEK !== null
            ? parseFloat(selected.HOURSPERWEEK)
            : '',
        contractStartDate: selected.CONTRACT_START_DATE || null,
        contractEndDate: selected.CONTRACT_END_DATE || null,
        managerName: selected.MANAGER_NAME ?? '',
        managerEmail: selected.MANAGER_EMAIL ?? '',
        coApproverName: selected.BACKUP_APPROVER_NAME ?? '',
        coApproverEmail: selected.BACKUP_APPROVER_EMAIL ?? '',
        assignmentType: selected.ASSIGNMENT_TYPE ?? '',
        workerPayCompany: selected.WORKER_PAY_COMPANY ?? '',
        payModel: selected.PAY_MODEL ?? '',
        expensesEnabled: selected.EXPENSES_ENABLED === '1',
        candidateCity: selected.CANDIDATE_CITY ?? '',
        candidateState: selected.CANDIDATE_STATE ?? '',

        postalCode: selected.POSTAL_CODE ?? '',
        billAddressSameAsWorkAddress: selected.BILL_ADDRESS_SAME_AS_WORK_ADDRESS === '1',

        payCost: selected.PAY_COST ?? '',

        projectCode: selected.PROJECT_CODE ?? '',
        costCenter: selected.COST_CENTER_NAME ?? '',
        department: selected.Departments ?? '',
        merId: selected.MER_ID ?? '',
        purchaseOrder: selected.PURCHASE_ORDER ?? '',
        assignmentStatus: selected.ASSIGNMENT_STATUS ?? '',

        billingPostalCOde: selected.BILLING_ZIP ?? '',
        glCode: selected.GL_CODE ?? '',
        externalReferenceId: selected.VMS_ID ?? '',

        expenses: resetExpenses,
      },
    });
  };

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFiles = (files: File[]) => {
    const updatedFiles = [...uploadedFiles, ...files];

    setUploadedFiles(updatedFiles);

    // Update Formik attachments
    formik.setFieldValue('attachments', updatedFiles);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    handleFiles(files);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer.files || []);

    handleFiles(files);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    formik.setFieldValue('attachments', updatedFiles);
  };

  const addExpenseRow = () => {
    formik.setFieldValue('expenses', [
      ...formik.values.expenses,
      {
        expenseDate: '',
        category: '',
        amount: '',
        receipt: null,
        details: '',
      },
    ]);
  };

  const removeExpenseRow = (index: number) => {
    const updatedExpenses = formik.values.expenses.filter((_, i) => i !== index);
    formik.setFieldValue('expenses', updatedExpenses);
  };

  return (
    <Container>
      {(isAssignmentFetching || addExpense.isLoading) && <FullScreenLoading />}

      <SearchLink>
        <Link to="/expenses">Search Expenses</Link>
      </SearchLink>

      <HeaderItemLeft>
        <NavTextLink to={-1 as any}>
          <ArrowBack src={Arrow} />
        </NavTextLink>
        <PageTitle>Create Expense Sheet</PageTitle>
      </HeaderItemLeft>

      <CardContainer>
        <HelpForm>
          <ModuleLine style={{ display: 'flex', gap: '36px', marginBottom: '20px' }}>
            <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
              <CommonDropdown
                label="Assignment *"
                id="assignmentId"
                name="assignmentId"
                options={assignmentOptionsWithBlank}
                value={formik.values.assignmentId}
                setValue={handleAssignmentChange}
                selectStyle={commonFieldStyle}
              />
              <FormErrorWrap
                errorMessage={formik.errors.assignmentId as string}
                visible={!!(formik.touched.assignmentId || formik.submitCount > 0) && !!formik.errors.assignmentId}
              />
            </FormInputWrapper>

            <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
              <WorkWeekDatepicker
                initialDate={selectedWorkWeekDate}
                onDateChange={(date: Date | null) => {
                  setSelectedWorkWeekDate(date);
                  formik.setFieldValue('workWeek', date);
                }}
                minDate={contractStartDate}
                maxDate={contractEndDate}
                // style={commonFieldStyle}
              />
              <FormErrorWrap
                errorMessage={formik.errors.workWeek as string}
                visible={!!(formik.touched.workWeek || formik.submitCount > 0) && !!formik.errors.workWeek}
              />
            </FormInputWrapper>
          </ModuleLine>

          {formik.values.expenses.map((expense, index) => (
            <ModuleLine key={index} style={{ display: 'flex', gap: '36px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '1.6rem', minWidth: '20px' }}>{index + 1}</span>

              <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
                <CommonDropdown
                  label={index === 0 ? 'Date of Expense *' : ''}
                  id={`expenses.${index}.expenseDate`}
                  name={`expenses.${index}.expenseDate`}
                  options={dateOfExpenseOptions}
                  value={expense.expenseDate}
                  setValue={(value: string) => formik.setFieldValue(`expenses.${index}.expenseDate`, value)}
                  selectStyle={commonFieldStyle}
                />
                <FormErrorWrap
                  errorMessage={(formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.expenseDate as string}
                  visible={
                    !!(
                      formik.submitCount > 0 &&
                      (formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.expenseDate
                    )
                  }
                />
              </FormInputWrapper>

              <FormInputWrapper style={{ flex: '1', maxWidth: '108%' }}>
                <CommonDropdown
                  label={index === 0 ? 'Category *' : ''}
                  id={`expenses.${index}.category`}
                  name={`expenses.${index}.category`}
                  options={expenseCategories.map(c => ({
                    key: c.name,
                    value: c.name,
                    label: c.displayName,
                  }))}
                  value={expense.category}
                  setValue={(value: string) => formik.setFieldValue(`expenses.${index}.category`, value)}
                  selectStyle={commonFieldStyle}
                />
                <FormErrorWrap
                  errorMessage={(formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.category as string}
                  visible={
                    !!(
                      formik.submitCount > 0 && (formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.category
                    )
                  }
                />
              </FormInputWrapper>

              <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
                <label style={{ display: index === 0 ? 'block' : 'none', marginBottom: '-2px', fontSize: '1.6rem' }}>
                  Amount *
                </label>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Currency Symbol OUTSIDE */}
                  <span
                    style={{
                      marginRight: '4px',
                      fontSize: '1.4rem',
                      marginTop: '8px',
                    }}
                  >
                    {formik?.values?.expenses?.[index]?.category === 'MILEAGE'
                      ? ''
                      : currencySymbol(formik?.values?.currency) || '$'}
                  </span>

                  {/* Input */}
                  <CommonInput
                    id={`expenses.${index}.amount`}
                    name={`expenses.${index}.amount`}
                    label=""
                    placeholder={formik?.values?.expenses?.[index]?.category === 'MILEAGE' ? 'Miles' : 'Money'}
                    value={expense.amount}
                    onChange={e => formik.setFieldValue(`expenses.${index}.amount`, e.target.value)}
                    autoComplete="off"
                    style={commonFieldStyle}
                  />

                  {/* RIGHT: Miles */}
                  <span
                    style={{
                      marginLeft: '4px',
                      fontSize: '1.4rem',
                      marginTop: '8px',
                    }}
                  >
                    {formik.values.expenses[index].category === 'MILEAGE' ? 'Miles' : ''}
                  </span>
                </div>

                <FormErrorWrap
                  errorMessage={(formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.amount as string}
                  visible={
                    !!(formik.submitCount > 0 && (formik.errors.expenses?.[index] as FormikErrors<ExpenseRow>)?.amount)
                  }
                />
              </FormInputWrapper>

              <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
                <UploadLink
                  label={index === 0 ? 'Receipt' : ''}
                  actionText={expense.receipt ? 'Replace' : 'Attach'}
                  onFileSelect={file => formik.setFieldValue(`expenses.${index}.receipt`, file)}
                />
              </FormInputWrapper>

              <FormInputWrapper style={{ flex: '1', maxWidth: '78%' }}>
                <CommonInput
                  id={`expenses.${index}.details`}
                  name={`expenses.${index}.details`}
                  label={index === 0 ? 'Details' : ''}
                  placeholder="Describe expense"
                  value={expense.details}
                  onChange={event => formik.setFieldValue(`expenses.${index}.details`, event.target.value)}
                  autoComplete="off"
                  style={commonFieldStyle}
                />
              </FormInputWrapper>

              <FormInputWrapper style={{ flex: '0 0 auto', marginTop: `${index === 0 ? '20px' : '0'}` }}>
                {formik.values.expenses.length > 1 ? (
                  <OutlinedButton type="button" onClick={() => removeExpenseRow(index)}>
                    Remove
                  </OutlinedButton>
                ) : null}
              </FormInputWrapper>
            </ModuleLine>
          ))}

          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '15px', marginTop: '10px' }}>
            <RegularButton type="button" onClick={addExpenseRow}>
              Add
            </RegularButton>
          </FormInputWrapper>

          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '5px', position: 'relative' }}>
            <label
              htmlFor="fileUpload"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '16px',
                color: '#222247',
              }}
            >
              Attachments
            </label>

            <input
              ref={fileInputRef}
              id="fileUpload"
              name="Attachment"
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <div
              style={{
                width: '100%',
                height: '136px',
                border: '1px solid #ccc',
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                boxSizing: 'border-box',
              }}
              onClick={handleFileUploadClick}
              onDragOver={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={handleFileDrop}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  font: '18px/27px Biotif',
                }}
              >
                <span
                  style={{
                    color: '#4A4BA9',
                    fontWeight: 'bold',
                    marginRight: '5px',
                  }}
                >
                  Add files
                </span>
                <span
                  style={{
                    color: '#222247',
                    opacity: 0.5,
                  }}
                >
                  Or drop files here
                </span>
              </div>
            </div>

            <FormErrorWrap
              errorMessage={formik.errors.attachments as string}
              visible={!!(formik.touched.attachments || formik.submitCount > 0) && !!formik.errors.attachments}
            />
          </FormInputWrapper>

          <FormInputWrapper style={{ flex: '0 0 100%', marginTop: '0px', marginBottom: '20px' }}>
            <div
              style={{
                width: '55%',
                marginTop: '0px',
                paddingTop: '0px',
                borderRadius: '7px',
              }}
            >
              {uploadedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} style={{ display: 'flex', alignItems: 'center', marginTop: '0px' }}>
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#4A4BA9',
                      textDecoration: 'none',
                      marginRight: '10px',
                      fontSize: '12px',
                    }}
                  >
                    {file.name}
                  </a>

                  <span
                    onClick={() => handleRemoveFile(index)}
                    style={{
                      color: '#4A4BA9',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </FormInputWrapper>

          <FormInputWrapper style={{ flex: '0 0 100%', marginBottom: '-10px' }}>
            <label htmlFor="notes" style={{ display: 'block', marginBottom: '5px' }}>
              Notes
            </label>

            <textarea
              id="notes"
              name="notes"
              placeholder="Notes"
              value={formik.values.notes}
              onChange={event => formik.setFieldValue('notes', event.target.value)}
              style={{
                width: '100%',
                height: '136px',
                borderRadius: '7px',
                border: '1px solid #ccc',
                boxSizing: 'border-box',
                padding: '10px',
                resize: 'none',
                fontSize: '14px',
                textAlign: 'left',
                verticalAlign: 'top',
              }}
            />

            <FormErrorWrap
              errorMessage={formik.errors.notes as string}
              visible={!!(formik.touched.notes && formik.errors.notes)}
            />
          </FormInputWrapper>

          <FormInputWrapper
            style={{
              flex: '0 0 100%',
              display: 'flex',
              justifyContent: 'flex-start',
              marginBottom: '5px',
            }}
          >
            <OutlinedButton onClick={() => navigate('/expenses')}>Cancel</OutlinedButton>

            <OutlinedButton
              type="button"
              onClick={() => {
                formik.setFieldValue('status', 'Draft');
                formik.handleSubmit();
              }}
              disabled={formik.isSubmitting || addExpense.isLoading}
            >
              Draft
            </OutlinedButton>

            <RegularButton
              type="button"
              onClick={() => {
                formik.setFieldValue('status', 'Submitted');
                formik.handleSubmit();
              }}
              disabled={formik.isSubmitting || addExpense.isLoading}
            >
              Submit
            </RegularButton>
          </FormInputWrapper>
        </HelpForm>
      </CardContainer>
    </Container>
  );
};

export default CreateExpense;
